import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiCheck,
  FiUpload,
  FiFileText,
  FiAward,
  FiMapPin,
  FiCalendar,
  FiCreditCard,
  FiShield,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiAlertTriangle
} from "react-icons/fi";
import "../../../assets/css/doctor/doctorpages/Kyc.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import { AppContext } from "../../../context/AppContext";

const Authentication = () => {
  const navigate = useNavigate();
  const { baseUrl, kycStatus, kycAttempts, kycData, kycLoading, fetchKycStatus } = useContext(AppContext);

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // ── Personal Information States ──────────────────────────
  const [fullName, setFullName]       = useState("");
  const [email, setEmail]             = useState("");
  const [phone, setPhone]             = useState("");
  const [cnic, setCnic]               = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender]           = useState("");
  const [address, setAddress]         = useState("");
  const [city, setCity]               = useState("");
  const [country]                     = useState("Pakistan");

  // ── Professional Information States ─────────────────────
  const [specialization, setSpecialization] = useState("");
  const [qualification, setQualification]   = useState("");
  const [institution, setInstitution]       = useState("");
  const [experience, setExperience]         = useState("");
  const [licenseNumber, setLicenseNumber]   = useState("");

  // ── Document State ───────────────────────────────────────
  const [documentFile, setDocumentFile] = useState(null);

  // ── Terms State ──────────────────────────────────────────
  const [termsAccepted, setTermsAccepted] = useState(false);

  // ── Verification States (dummy abhi ke liye) ─────────────
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  // ── Redirect if already approved ──────────────────────────
  useEffect(() => {
    if (!kycLoading && kycStatus === "approved") {
      navigate("/doctor");
    }
  }, [kycLoading, kycStatus, navigate]);

  // ── Prefill form if rejected (so therapist can edit & resubmit) ──
  useEffect(() => {
    if (kycStatus === "rejected" && kycData) {
      setFullName(kycData.full_name || "");
      setGender(kycData.gender || "");
      setEmail(kycData.email || "");
      setPhone(kycData.phone || "");
      setCnic(kycData.cnic || "");
      setDateOfBirth(kycData.date_of_birth ? kycData.date_of_birth.split("T")[0] : "");
      setAddress(kycData.address || "");
      setCity(kycData.city || "");
      setSpecialization(kycData.specialization || "");
      setQualification(kycData.qualification || "");
      setInstitution(kycData.institution || "");
      setExperience(kycData.experience || "");
      setLicenseNumber(kycData.license_number || "");
      if (kycData.email) setEmailVerified(true);
      if (kycData.phone) setPhoneVerified(true);
    }
  }, [kycStatus, kycData]);

  const handleNext = () => {
    // ── Step 1 validation ──
    if (currentStep === 1) {
      if (!fullName || !gender || !email || !phone || !cnic || !dateOfBirth) {
        Swal.fire({ icon: "warning", title: "Incomplete", text: "Please fill all required fields in Personal Information." });
        return;
      }
    }
    // ── Step 2 validation ──
    if (currentStep === 2) {
      if (!specialization || !qualification || !experience || !licenseNumber) {
        Swal.fire({ icon: "warning", title: "Incomplete", text: "Please fill all required fields in Professional Information." });
        return;
      }
    }
    setCurrentStep(currentStep + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = () => {
    if (!documentFile) {
      Swal.fire({ icon: "warning", title: "Document Required", text: "Please upload your documents PDF." });
      return;
    }
    if (!termsAccepted) {
      Swal.fire({ icon: "warning", title: "Terms Required", text: "Please accept the Terms of Service to continue." });
      return;
    }

    const token = localStorage.getItem("doctor_token");
    if (!token) {
      Swal.fire({ icon: "error", title: "Session Expired", text: "Please login again." });
      navigate("/");
      return;
    }

    const formData = new FormData();
    formData.append("full_name", fullName);
    formData.append("gender", gender);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("cnic", cnic);
    formData.append("date_of_birth", dateOfBirth);
    formData.append("address", address);
    formData.append("city", city);
    formData.append("country", country);
    formData.append("specialization", specialization);
    formData.append("qualification", qualification);
    formData.append("institution", institution);
    formData.append("experience", experience);
    formData.append("license_number", licenseNumber);
    formData.append("document", documentFile);

    setSubmitting(true);

    axios.post(`${baseUrl}/doctor/Kyc/submit`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
    })
      .then(() => {
        setSubmitting(false);
        Swal.fire({
          icon: "success",
          title: "KYC Submitted!",
          text: "Your account is under review. We'll notify you once it's verified.",
        });
        fetchKycStatus(); // refresh status -> will show "pending" screen
      })
      .catch((error) => {
        setSubmitting(false);
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: error?.response?.data?.message || "Something went wrong. Please try again.",
        });
      });
  };

  const steps = [
    { number: 1, title: "Personal Info",  icon: <FiUser />     },
    { number: 2, title: "Professional",   icon: <FiAward />    },
    { number: 3, title: "Documents",      icon: <FiFileText /> }
  ];

  const pageHeader = (
    <div className="kyc-header">
      <h2 className="kyc-title">KYC Verification</h2>
      <p className="kyc-subtitle">Complete your identity and professional verification to activate your account</p>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════════════
  if (kycLoading) {
    return (
      <DoctorMasterLayout>
        <div className="kyc-container">
          <div className="kyc-content">
            <div className="kyc-card">
              <p className="kyc-loading">Loading your KYC status...</p>
            </div>
          </div>
        </div>
      </DoctorMasterLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // PENDING STATE
  // ═══════════════════════════════════════════════════════════
  if (kycStatus === "pending") {
    return (
      <DoctorMasterLayout>
        <div className="kyc-container">
          <div className="kyc-content">
            {pageHeader}
            <div className="kyc-card kyc-state-card">
              <FiClock className="kyc-state-icon kyc-state-icon-pending" />
              <h2 className="kyc-state-title">Your KYC is Under Review</h2>
              <p className="kyc-state-text">
                We're verifying your submitted documents. This usually takes 24–48 hours.
                You'll be notified once it's approved.
              </p>
            </div>
          </div>
        </div>
      </DoctorMasterLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // BLOCKED STATE
  // ═══════════════════════════════════════════════════════════
  if (kycStatus === "blocked") {
    return (
      <DoctorMasterLayout>
        <div className="kyc-container">
          <div className="kyc-content">
            {pageHeader}
            <div className="kyc-card kyc-state-card">
              <FiXCircle className="kyc-state-icon kyc-state-icon-blocked" />
              <h2 className="kyc-state-title">Maximum Attempts Reached</h2>
              <p className="kyc-state-text">
                You've reached the maximum number of KYC submission attempts (3).
                Please contact our support team for further assistance.
              </p>
            </div>
          </div>
        </div>
      </DoctorMasterLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // MAIN FORM (not_submitted OR rejected)
  // ═══════════════════════════════════════════════════════════
  return (
    <DoctorMasterLayout>
      <div className="kyc-container">
        <div className="kyc-content">

          {pageHeader}

          {/* ── Rejection Banner ────────────────────────────── */}
          {kycStatus === "rejected" && (
            <div className="kyc-alert-rejected">
              <FiAlertTriangle className="kyc-alert-icon" />
              <div>
                <strong className="kyc-alert-title">Your previous KYC was rejected</strong>
                <p className="kyc-alert-text">
                  Reason: {kycData?.rejection_reason || "No reason provided."}
                </p>
                <p className="kyc-alert-meta">
                  Attempts used: {kycAttempts} / 3. Please correct the issue and resubmit below.
                </p>
              </div>
            </div>
          )}

          {/* ── Progress Steps ─────────────────────────────── */}
          <div className="kyc-progress-steps">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div
                  className={`kyc-step${currentStep === step.number ? " kyc-step-active" : ""}${currentStep > step.number ? " kyc-step-completed" : ""}`}
                >
                  <div className="kyc-step-icon">
                    {currentStep > step.number ? <FiCheckCircle /> : step.icon}
                  </div>
                  <div className="kyc-step-info">
                    <span className="kyc-step-number">Step {step.number}</span>
                    <span className="kyc-step-title">{step.title}</span>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`kyc-step-line${currentStep > step.number ? " kyc-step-line-completed" : ""}`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* ── Main Card ──────────────────────────────────── */}
          <div className="kyc-card">
            <div>

              {/* ════════════ STEP 1 : Personal Info ════════════ */}
              {currentStep === 1 && (
                <div className="kyc-form-step">
                  <div className="kyc-step-header">
                    <div className="kyc-step-header-icon kyc-icon-personal">
                      <FiUser />
                    </div>
                    <div className="kyc-step-header-text">
                      <h3>Personal Information</h3>
                      <p>Please provide your basic personal details</p>
                    </div>
                  </div>

                  <div className="kyc-form-section">
                    <div className="kyc-form-row">
                      <div className="kyc-form-group">
                        <label htmlFor="fullName">
                          <FiUser className="kyc-label-icon" />
                          Full Name <span className="kyc-required">*</span>
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          placeholder="Enter your name"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                        />
                      </div>

                      <div className="kyc-form-group">
                        <label htmlFor="gender">
                          <FiUser className="kyc-label-icon" />
                          Gender <span className="kyc-required">*</span>
                        </label>
                        <select
                          id="gender"
                          value={gender}
                          onChange={(e) => setGender(e.target.value)}
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="kyc-form-row">
                      <div className="kyc-form-group">
                        <label htmlFor="email">
                          <FiMail className="kyc-label-icon" />
                          Email Address <span className="kyc-required">*</span>
                        </label>
                        <div className="kyc-input-with-action">
                          <input
                            type="email"
                            id="email"
                            placeholder="@gmail.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                          <button
                            type="button"
                            className={`kyc-verify-btn${emailVerified ? " kyc-verify-btn-verified" : ""}`}
                            onClick={() => { if (email) setEmailVerified(true); }}
                            disabled={emailVerified}
                          >
                            {emailVerified ? <><FiCheck /> Verified</> : "Verify"}
                          </button>
                        </div>
                      </div>

                      <div className="kyc-form-group">
                        <label htmlFor="phone">
                          <FiPhone className="kyc-label-icon" />
                          Phone Number <span className="kyc-required">*</span>
                        </label>
                        <div className="kyc-input-with-action">
                          <input
                            type="tel"
                            id="phone"
                            placeholder="Enter your phone number"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                          />
                          <button
                            type="button"
                            className={`kyc-verify-btn${phoneVerified ? " kyc-verify-btn-verified" : ""}`}
                            onClick={() => { if (phone) setPhoneVerified(true); }}
                            disabled={phoneVerified}
                          >
                            {phoneVerified ? <><FiCheck /> Verified</> : "Verify"}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="kyc-form-row">
                      <div className="kyc-form-group">
                        <label htmlFor="cnic">
                          <FiCreditCard className="kyc-label-icon" />
                          CNIC Number <span className="kyc-required">*</span>
                        </label>
                        <input
                          type="text"
                          id="cnic"
                          placeholder="00000-0000000-0"
                          value={cnic}
                          onChange={(e) => setCnic(e.target.value)}
                        />
                      </div>

                      <div className="kyc-form-group">
                        <label htmlFor="dateOfBirth">
                          <FiCalendar className="kyc-label-icon" />
                          Date of Birth <span className="kyc-required">*</span>
                        </label>
                        <input
                          type="date"
                          id="dateOfBirth"
                          value={dateOfBirth}
                          onChange={(e) => setDateOfBirth(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="kyc-form-group kyc-full-width">
                      <label htmlFor="address">
                        <FiMapPin className="kyc-label-icon" />
                        Complete Address
                      </label>
                      <input
                        type="text"
                        id="address"
                        placeholder="Enter your address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                    </div>

                    <div className="kyc-form-row">
                      <div className="kyc-form-group">
                        <label htmlFor="city">
                          <FiMapPin className="kyc-label-icon" />
                          City
                        </label>
                        <input
                          type="text"
                          id="city"
                          placeholder="Enter your city"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                        />
                      </div>

                      <div className="kyc-form-group">
                        <label htmlFor="country">
                          <FiMapPin className="kyc-label-icon" />
                          Country
                        </label>
                        <input
                          type="text"
                          id="country"
                          value={country}
                          readOnly
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ════════════ STEP 2 : Professional Info ════════ */}
              {currentStep === 2 && (
                <div className="kyc-form-step">
                  <div className="kyc-step-header">
                    <div className="kyc-step-header-icon kyc-icon-professional">
                      <FiAward />
                    </div>
                    <div className="kyc-step-header-text">
                      <h3>Professional Information</h3>
                      <p>Provide your credentials and professional details</p>
                    </div>
                  </div>

                  <div className="kyc-form-section">
                    <div className="kyc-form-row">
                      <div className="kyc-form-group">
                        <label htmlFor="specialization">
                          <FiAward className="kyc-label-icon" />
                          Specialization <span className="kyc-required">*</span>
                        </label>
                        <select
                          id="specialization"
                          value={specialization}
                          onChange={(e) => setSpecialization(e.target.value)}
                        >
                          <option value="">Select Specialization</option>
                          <option value="speech_therapist">Speech Therapist</option>
                          <option value="audiologist">Audiologist</option>
                          <option value="language_pathologist">Language Pathologist</option>
                          <option value="voice_therapist">Voice Therapist</option>
                          <option value="fluency_specialist">Fluency Specialist</option>
                        </select>
                      </div>

                      <div className="kyc-form-group">
                        <label htmlFor="experience">
                          <FiCalendar className="kyc-label-icon" />
                          Years of Experience <span className="kyc-required">*</span>
                        </label>
                        <input
                          type="number"
                          id="experience"
                          placeholder="Years"
                          value={experience}
                          onChange={(e) => setExperience(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="kyc-form-row">
                      <div className="kyc-form-group">
                        <label htmlFor="qualification">
                          <FiAward className="kyc-label-icon" />
                          Highest Qualification <span className="kyc-required">*</span>
                        </label>
                        <input
                          type="text"
                          id="qualification"
                          placeholder="e.g., MBBS, MD"
                          value={qualification}
                          onChange={(e) => setQualification(e.target.value)}
                        />
                      </div>

                      <div className="kyc-form-group">
                        <label htmlFor="institution">
                          <FiAward className="kyc-label-icon" />
                          Institution Name
                        </label>
                        <input
                          type="text"
                          id="institution"
                          placeholder="University / College Name"
                          value={institution}
                          onChange={(e) => setInstitution(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="kyc-form-group kyc-full-width">
                      <label htmlFor="licenseNumber">
                        <FiShield className="kyc-label-icon" />
                        License Number <span className="kyc-required">*</span>
                      </label>
                      <input
                        type="text"
                        id="licenseNumber"
                        placeholder="Medical License Number"
                        value={licenseNumber}
                        onChange={(e) => setLicenseNumber(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ════════════ STEP 3 : Documents ════════════════ */}
              {currentStep === 3 && (
                <div className="kyc-form-step">
                  <div className="kyc-step-header">
                    <div className="kyc-step-header-icon kyc-icon-documents">
                      <FiFileText />
                    </div>
                    <div className="kyc-step-header-text">
                      <h3>Document Verification</h3>
                      <p>Upload your documents for verification (PDF only, max 5 MB)</p>
                    </div>
                  </div>

                  <div className="kyc-form-section">

                    {/* Required docs list */}
                    <div className="kyc-doc-required-list">
                      <h4 className="kyc-doc-list-title">
                        <FiFileText /> Required Documents
                      </h4>
                      <ul className="kyc-doc-list">
                        <li><FiCreditCard /> CNIC Front Side <span className="kyc-required">(Required)</span></li>
                        <li><FiCreditCard /> CNIC Back Side <span className="kyc-required">(Required)</span></li>
                        <li><FiShield />     Medical License <span className="kyc-required">(Required)</span></li>
                        <li><FiAward />      Degree Certificate <span className="kyc-required">(Required)</span></li>
                      </ul>
                      <p className="kyc-doc-hint">
                        Please merge all required documents into one PDF before uploading.
                      </p>
                    </div>

                    {/* Single PDF upload */}
                    <div className="kyc-upload-card">
                      <div className="kyc-upload-icon">
                        <FiUpload />
                      </div>
                      <h4>
                        Upload Documents PDF <span className="kyc-required">(Required)</span>
                      </h4>
                      <p>CNIC (front &amp; back), Medical License, Degree Certificate</p>

                      <label htmlFor="documentFile" className="kyc-upload-btn">
                        <FiUpload />
                        {documentFile ? "Change File" : "Choose PDF File"}
                        <input
                          type="file"
                          id="documentFile"
                          accept=".pdf"
                          className="kyc-file-input-hidden"
                          onChange={(e) => {
                            const f = e.target.files[0];
                            if (f && f.size > 5 * 1024 * 1024) {
                              Swal.fire({ icon: "warning", title: "File Too Large", text: "File size should be less than 5MB" });
                              return;
                            }
                            setDocumentFile(f || null);
                          }}
                        />
                      </label>

                      {documentFile && (
                        <div className="kyc-file-selected">
                          <FiCheckCircle /> {documentFile.name}
                        </div>
                      )}
                    </div>

                    {/* Terms */}
                    <div className="kyc-terms-section">
                      <label className="kyc-checkbox-label">
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                        />
                        <span>
                          I agree to the{" "}
                          <a href="#terms"   target="_blank" rel="noopener noreferrer">Terms of Service</a>,{" "}
                          <a href="#privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, and{" "}
                          <a href="#kyc"     target="_blank" rel="noopener noreferrer">KYC Guidelines</a>
                        </span>
                      </label>
                    </div>

                    {/* Review box */}
                    <div className="kyc-review-box">
                      <h4><FiCheckCircle /> Ready to Submit</h4>
                      <p>
                        Please review all information before submitting. Your KYC will be
                        verified within 24–48 hours.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Navigation Buttons ───────────────────────── */}
              <div className="kyc-form-navigation">
                {currentStep > 1 && (
                  <button
                    type="button"
                    className="kyc-btn-secondary"
                    onClick={handlePrevious}
                    disabled={submitting}
                  >
                    Previous
                  </button>
                )}

                {currentStep < 3 ? (
                  <button
                    type="button"
                    className="kyc-btn-primary"
                    onClick={handleNext}
                  >
                    Next Step
                  </button>
                ) : (
                  <button
                    type="button"
                    className="kyc-btn-submit"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    <FiCheckCircle /> {submitting ? "Submitting..." : "Submit KYC"}
                  </button>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </DoctorMasterLayout>
  );
};

export default Authentication;